import type { QuotationInvoiceCustomerCategoryListItem } from "@/modules/quotation-invoice/domain/types";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import {
    Button,
    FormCheckbox,
    FormInput,
    FormRoot,
    Modal,
} from "@devhop/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { FolderTreeIcon, PencilIcon, PlusIcon } from "lucide-react";
import * as RHF from "react-hook-form";
import { z } from "zod";
import {
    useCreateQuotationInvoiceCustomerCategory,
    useQuotationInvoiceCustomerCategoriesQuery,
    useUpdateQuotationInvoiceCustomerCategory,
} from "../api/queries";

const CreateCategorySchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຫມວດຫມູ່"),
  sortOrder: z.string().optional(),
});

const EditCategorySchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຫມວດຫມູ່"),
  sortOrder: z.string().optional(),
  isActive: z.boolean().optional(),
});

type CreateCategoryValues = z.infer<typeof CreateCategorySchema>;
type EditCategoryValues = z.infer<typeof EditCategorySchema>;

export function CustomerCategoryManagementModal({
  open,
  onOpenChange,
  onRefresh,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefresh?: () => void;
}) {
  const createModal = useDisclosure();
  const editModal = useDisclosure<QuotationInvoiceCustomerCategoryListItem>();

  const { data, refetch } = useQuotationInvoiceCustomerCategoriesQuery({
    includeInactive: true,
  });
  const categories = data?.items ?? [];

  const createCategory = useCreateQuotationInvoiceCustomerCategory();
  const updateCategory = useUpdateQuotationInvoiceCustomerCategory();

  const refresh = () => {
    refetch();
    onRefresh?.();
  };

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title="ຈັດການຫມວດຫມູ່ລູກຄ້າ"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={createModal.open}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              ເພີ່ມຫມວດຫມູ່
            </Button>
          </div>

          <div className="rounded-lg border">
            <div className="max-h-[calc(100vh-16rem)] divide-y overflow-y-auto">
              {categories.length === 0 ? (
                <div className="flex flex-col items-center gap-2 p-8 text-center text-muted-foreground text-sm">
                  <FolderTreeIcon className="h-10 w-10 opacity-50" />
                  <p>ຍັງບໍ່ມີຫມວດຫມູ່ — ເພີ່ມແລ້ວໃຫ້ລູກຄ້າໃນຟອມແກ້ໄຂ</p>
                </div>
              ) : (
                categories.map((cat) => (
                  <div
                    key={cat.id}
                    className="flex items-center justify-between gap-2 p-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={
                            cat.isActive
                              ? "font-medium"
                              : "font-medium text-muted-foreground line-through"
                          }
                        >
                          {cat.name}
                        </span>
                        <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
                          ລຳດັບ {cat.sortOrder}
                        </span>
                        {!cat.isActive && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
                            ປິດໃຊ້ງານ
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => editModal.openWith(cat)}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!createModal.isOpen}
        onOpenChange={createModal.toggle}
        title="ເພີ່ມຫມວດຫມູ່ລູກຄ້າ"
        size="sm"
      >
        <CreateCategoryForm
          onSubmit={async (vals) => {
            const sort = Number.parseInt(vals.sortOrder?.trim() || "0", 10);
            await createCategory.mutateAsync({
              name: vals.name.trim(),
              sortOrder: Number.isFinite(sort) ? sort : 0,
            });
            createModal.close();
            refresh();
          }}
          submitting={createCategory.isPending}
        />
      </Modal>

      <Modal
        open={!!editModal.isOpen}
        onOpenChange={editModal.toggle}
        title="ແກ້ໄຂຫມວດຫມູ່ລູກຄ້າ"
        size="sm"
      >
        {editModal.data ? (
          <EditCategoryForm
            key={editModal.data.id}
            initial={editModal.data}
            submitting={updateCategory.isPending}
            onSubmit={async (vals) => {
              if (!editModal.data) return;
              const sort = Number.parseInt(vals.sortOrder?.trim() || "0", 10);
              await updateCategory.mutateAsync({
                id: editModal.data.id,
                name: vals.name.trim(),
                sortOrder: Number.isFinite(sort) ? sort : 0,
                isActive: vals.isActive,
              });
              editModal.close();
              refresh();
            }}
          />
        ) : null}
      </Modal>
    </>
  );
}

function CreateCategoryForm({
  onSubmit,
  submitting,
}: {
  onSubmit: (vals: CreateCategoryValues) => void | Promise<void>;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<CreateCategoryValues>({
    resolver: zodResolver(CreateCategorySchema),
    defaultValues: { name: "", sortOrder: "0" },
  });

  return (
    <FormRoot<CreateCategoryValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <FormInput
        name="name"
        label="ຊື່ຫມວດຫມູ່"
        placeholder="ຕົວຢ່າງ: ລູກຄ້າອົງກອນ"
        requiredMark
      />
      <FormInput
        name="sortOrder"
        label="ລຳດັບສະແດງ (ເລກນ້ອຍຂຶ້ນກ່ອນ)"
        placeholder="0"
      />
      <div className="flex justify-end">
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}

function EditCategoryForm({
  initial,
  onSubmit,
  submitting,
}: {
  initial: QuotationInvoiceCustomerCategoryListItem;
  onSubmit: (vals: EditCategoryValues) => void | Promise<void>;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<EditCategoryValues>({
    resolver: zodResolver(EditCategorySchema),
    defaultValues: {
      name: initial.name,
      sortOrder: String(initial.sortOrder),
      isActive: initial.isActive,
    },
  });

  return (
    <FormRoot<EditCategoryValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <FormInput name="name" label="ຊື່ຫມວດຫມູ່" requiredMark />
      <FormInput name="sortOrder" label="ລຳດັບສະແດງ" />
      <FormCheckbox name="isActive" label="ໃຊ້ງານຢູ່" />
      <div className="flex justify-end">
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
